import { useNavigation } from '@refinedev/core';
import { List, useTable, CreateButton } from '@refinedev/antd';
import { Table, Input, Space, Tag, Button, Select } from 'antd';
import { SearchOutlined, EyeOutlined } from '@ant-design/icons';
import { useState } from 'react';

interface BusCompany {
  id: number;
  name: string;
  legalName: string;
  contactPhone: string;
  contactEmail: string | null;
  status: 'ONLINE' | 'OFFLINE' | 'BLOCKED';
  balance: number;
  commissionPercent: number;
  _count: {
    busTrips: number;
  };
  createdAt: string;
}

export const BusCompanyList = () => {
  const { show } = useNavigation();
  const [searchText, setSearchText] = useState('');

  const { tableProps, setFilters } = useTable<BusCompany>({
    resource: 'admin/bus/companies',
    syncWithLocation: true,
  });

  const handleSearch = () => {
    const filters: any[] = [];
    if (searchText) {
      filters.push({ field: 'search', operator: 'contains', value: searchText });
    }
    setFilters(filters);
  };

  const handleStatusFilter = (status: string | undefined) => {
    const filters: any[] = [];
    if (status) {
      filters.push({ field: 'status', operator: 'eq', value: status });
    }
    if (searchText) {
      filters.push({ field: 'search', operator: 'contains', value: searchText });
    }
    setFilters(filters);
  };

  const statusColors: Record<string, string> = {
    ONLINE: 'green',
    OFFLINE: 'orange',
    BLOCKED: 'red',
  };

  const statusLabels: Record<string, string> = {
    ONLINE: 'Faol',
    OFFLINE: 'Oflayn',
    BLOCKED: 'Bloklangan',
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + " so'm";
  };

  const columns = [
    {
      title: 'Kompaniya',
      dataIndex: 'name',
      key: 'name',
      render: (_: string, record: BusCompany) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.name}</div>
          <div style={{ color: '#7F8C8D', fontSize: 12 }}>{record.legalName}</div>
        </div>
      ),
    },
    {
      title: 'Telefon',
      dataIndex: 'contactPhone',
      key: 'contactPhone',
    },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status] || 'default'}>{statusLabels[status] || status}</Tag>
      ),
    },
    {
      title: 'Balans',
      dataIndex: 'balance',
      key: 'balance',
      render: (balance: number) => formatCurrency(balance || 0),
    },
    {
      title: 'Komissiya',
      dataIndex: 'commissionPercent',
      key: 'commissionPercent',
      render: (value: number) => `${value || 0}%`,
    },
    {
      title: 'Reyslar',
      key: 'tripsCount',
      render: (_: unknown, record: BusCompany) => record._count?.busTrips || 0,
    },
    {
      title: "Ro'yxatdan",
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('uz-UZ'),
    },
    {
      title: 'Amallar',
      key: 'actions',
      render: (_: unknown, record: BusCompany) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => show('admin/bus/companies', record.id)}
        />
      ),
    },
  ];

  return (
    <List
      title="Avtobus kompaniyalari"
      headerButtons={
        <Space>
          <Input
            placeholder="Qidirish..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 200 }}
            onPressEnter={handleSearch}
          />
          <Select
            placeholder="Holat"
            allowClear
            style={{ width: 130 }}
            onChange={handleStatusFilter}
            options={[
              { value: 'ONLINE', label: 'Faol' },
              { value: 'OFFLINE', label: 'Oflayn' },
              { value: 'BLOCKED', label: 'Bloklangan' },
            ]}
          />
          <CreateButton>Yangi kompaniya</CreateButton>
        </Space>
      }
    >
      <Table {...tableProps} columns={columns} rowKey="id" />
    </List>
  );
};
